import {
  type ElementType,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

interface RevealProps {
  children: ReactNode;
  delay?: number;
  as?: ElementType;
  style?: React.CSSProperties;
  className?: string;
}

export function Reveal({
  children,
  delay = 0,
  as: Tag = 'div',
  style,
  className = '',
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return;
    }

    const checkInView = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      return r.top < vh * 0.95 && r.bottom > 0;
    };

    if (checkInView()) {
      setTimeout(() => setVisible(true), delay);
      return;
    }

    let io: IntersectionObserver;
    try {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              setTimeout(() => setVisible(true), delay);
              io.unobserve(el);
            }
          });
        },
        { threshold: 0.1, rootMargin: '0px 0px -5% 0px' },
      );
      io.observe(el);
    } catch {
      setVisible(true);
      return;
    }

    const onScroll = () => {
      if (checkInView()) {
        setVisible(true);
        io?.disconnect();
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const fallback = setTimeout(() => {
      if (checkInView()) setVisible(true);
    }, 1500);

    return () => {
      io?.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(fallback);
    };
  }, [delay]);

  const Comp = Tag as React.ElementType;
  return (
    <Comp
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={style}
    >
      {children}
    </Comp>
  );
}
